import { useQueries, useQuery } from '@tanstack/react-query';
import { useReactTable, getCoreRowModel, getSortedRowModel, flexRender, type ColumnDef, type SortingState } from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import type { components } from '@trackercop/contracts';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-keys';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/table';
import { TriggerScanButton } from '@/components/trigger-scan-button';
import { DeleteSiteButton } from '@/components/delete-site-button';
import { CrawlStatusBadge } from '@/components/crawl-status-badge';
import { BaselineStatusBadge, DriftSeverityBadge } from '@/components/baseline-badge';
import { formatDateTime } from '@/lib/format';

type Site = components['schemas']['Site'];
type SiteWithSummary = components['schemas']['SiteWithSummary'];
type DriftReport = components['schemas']['DriftReport'];

interface SiteRow {
  site: Site;
  summary?: SiteWithSummary | undefined;
  drift?: DriftReport | undefined;
}

function deriveDriftSeverity(drift: DriftReport | undefined): 'none' | 'minor' | 'major' | null {
  if (!drift) return null;
  const newPayloadCats = drift.newPayloadCategories ?? [];
  if (newPayloadCats.some((c) => c === 'pii' || c === 'identifier' || c === 'device-fingerprint')) {
    return 'major';
  }
  if (newPayloadCats.some((c) => c === 'advertising' || c === 'behavioral')) {
    return 'minor';
  }
  const newScripts = drift.newScripts?.length ?? 0;
  const newDests = drift.newDestinations?.length ?? 0;
  const newTags = drift.newGTMTags?.length ?? 0;
  if (newScripts + newDests + newTags === 0) return 'none';
  return 'minor';
}

// Columns definition is schema — no closure over component state. Moved
// outside the component body so it has a stable reference across renders.
// Previously this was declared inside SitesTable, which meant every
// render created a fresh array with new cell-function references;
// TanStack Table treated that as a column-schema change and rebuilt its
// entire model, cascading into thousands of re-renders per second and
// swallowing clicks via a reflow-thrash loop.
const sitesTableColumns: ColumnDef<SiteRow>[] = [
  {
    id: 'name',
    header: 'Name',
    accessorFn: (row) => row.site.name,
    cell: ({ row }) => (
      <Link
        to="/sites/$siteId"
        params={{ siteId: row.original.site.id }}
        className="font-medium text-primary underline-offset-4 hover:underline"
      >
        {row.original.site.name}
      </Link>
    ),
  },
  {
    id: 'baseUrl',
    header: 'Base URL',
    accessorFn: (row) => row.site.baseUrl,
    cell: ({ getValue }) => <span className="font-mono text-xs">{String(getValue())}</span>,
  },
  {
    id: 'lastCrawlDate',
    header: 'Last crawl',
    accessorFn: (row) => row.summary?.latestCrawl?.startedAt ?? '',
    cell: ({ row }) => formatDateTime(row.original.summary?.latestCrawl?.startedAt ?? null),
  },
  {
    id: 'lastCrawlStatus',
    header: 'Crawl status',
    accessorFn: (row) => row.summary?.latestCrawl?.status ?? '',
    cell: ({ row }) =>
      row.original.summary?.latestCrawl ? (
        <CrawlStatusBadge status={row.original.summary.latestCrawl.status} />
      ) : (
        <span className="text-xs text-muted-foreground">No crawls yet</span>
      ),
  },
  {
    id: 'baselineStatus',
    header: 'Baseline',
    cell: ({ row }) => (
      <BaselineStatusBadge
        baseline={row.original.summary?.currentBaseline}
        drift={row.original.drift ?? null}
      />
    ),
  },
  {
    id: 'driftSeverity',
    header: 'Drift severity',
    cell: ({ row }) => <DriftSeverityBadge severity={deriveDriftSeverity(row.original.drift)} />,
  },
  {
    id: 'actions',
    header: '',
    cell: ({ row }) => (
      <div className="flex justify-end gap-2">
        <TriggerScanButton siteId={row.original.site.id} />
        <Link
          to="/sites/$siteId"
          params={{ siteId: row.original.site.id }}
          className="inline-flex h-8 items-center rounded-md px-3 text-xs font-medium hover:bg-muted"
        >
          View
        </Link>
        <DeleteSiteButton
          siteId={row.original.site.id}
          siteName={row.original.site.name}
        />
      </div>
    ),
  },
];

export function SitesTable() {
  const [sorting, setSorting] = useState<SortingState>([]);

  const sitesQuery = useQuery({
    queryKey: queryKeys.sites.list(1, 200),
    queryFn: async () => {
      const { data, error } = await apiClient.GET('/sites', {
        params: { query: { page: 1, pageSize: 200 } },
      });
      if (error) throw error;
      return data;
    },
  });

  const sites = sitesQuery.data?.items ?? [];

  const summaryQueries = useQueries({
    queries: sites.map((s) => ({
      queryKey: queryKeys.sites.detail(s.id),
      queryFn: async (): Promise<SiteWithSummary> => {
        const { data, error } = await apiClient.GET('/sites/{siteId}', {
          params: { path: { siteId: s.id } },
        });
        if (error) throw error;
        return data;
      },
      enabled: sites.length > 0,
    })),
  });

  const driftQueries = useQueries({
    queries: sites.map((s) => ({
      queryKey: queryKeys.sites.drift(s.id),
      queryFn: async (): Promise<DriftReport | null> => {
        const { data, error } = await apiClient.GET('/sites/{siteId}/drift', {
          params: { path: { siteId: s.id } },
        });
        // 404 is expected for sites without a baseline — surface as null.
        if (error) return null;
        return data;
      },
      enabled: sites.length > 0,
      retry: false,
    })),
  });

  // Memoise the derived row list so TanStack Table sees a stable
  // reference across renders. The deps are the stable inputs:
  // the sites list itself, and the ready-state of each per-site
  // summary / drift query. A referentially new `rows` array each
  // render would make TanStack Table rebuild its model, cascading
  // into the render loop that was swallowing clicks.
  const summaryStatusKey = summaryQueries.map((q) => q.dataUpdatedAt).join(',');
  const driftStatusKey = driftQueries.map((q) => q.dataUpdatedAt).join(',');
  const rows: SiteRow[] = useMemo(
    () =>
      sites.map((site, idx) => ({
        site,
        summary: summaryQueries[idx]?.data,
        drift: driftQueries[idx]?.data ?? undefined,
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sites, summaryStatusKey, driftStatusKey],
  );

  const table = useReactTable({
    data: rows,
    columns: sitesTableColumns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  if (sitesQuery.isLoading) {
    return <p className="text-sm text-muted-foreground">Loading sites…</p>;
  }
  if (sitesQuery.isError) {
    return <p className="text-sm text-red-600">Failed to load sites.</p>;
  }

  return (
    <Table>
      <THead>
        {table.getHeaderGroups().map((group) => (
          <TR key={group.id}>
            {group.headers.map((header) => (
              <TH
                key={header.id}
                onClick={header.column.getToggleSortingHandler()}
                className={header.column.getCanSort() ? 'cursor-pointer select-none' : ''}
              >
                {flexRender(header.column.columnDef.header, header.getContext())}
                {header.column.getIsSorted() === 'asc' ? ' ↑' : header.column.getIsSorted() === 'desc' ? ' ↓' : ''}
              </TH>
            ))}
          </TR>
        ))}
      </THead>
      <TBody>
        {table.getRowModel().rows.length === 0 ? (
          <TR>
            <TD colSpan={sitesTableColumns.length} className="text-center text-sm text-muted-foreground">
              No sites yet.
            </TD>
          </TR>
        ) : (
          table.getRowModel().rows.map((row) => (
            <TR key={row.id} data-testid={`site-row-${row.original.site.id}`}>
              {row.getVisibleCells().map((cell) => (
                <TD key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TD>
              ))}
            </TR>
          ))
        )}
      </TBody>
    </Table>
  );
}
